import { isDrivable, latLngToLocalMeters, roadWidthMeters } from '@roadworld/map-generator';
import type { ChunkPayload, RoadClass } from '@roadworld/shared';

export type SurfaceKind = 'road' | 'path' | 'grass';

/** Richer per-segment metadata returned by `classifyRoadDetailAt` for the debug HUD. */
export interface RoadDetail {
  roadClass: RoadClass;
  lanes?: number;
  oneway: boolean;
  bridge: boolean;
  tunnel: boolean;
  drivable: boolean;
  /** Half-width of the segment's road in meters. */
  halfWidth: number;
}

interface RoadSegment {
  /** Endpoints in chunk-local meters (origin = chunk center). */
  ax: number;
  az: number;
  bx: number;
  bz: number;
  /** Half the road's width (m), the perpendicular distance within which a point is "on" it. */
  halfWidth: number;
  /** Drivable roads (cars) vs pedestrian paths (footway, cycleway, etc). */
  drivable: boolean;
  /** OSM classification — preserved so the debug HUD can report it back. */
  roadClass: RoadClass;
  lanes?: number;
  oneway: boolean;
  bridge: boolean;
  tunnel: boolean;
}

/**
 * Per-chunk uniform-grid index over road/path segments. Queries are point-to-segment
 * distance checks over a tiny candidate set per cell — fast enough to run every
 * physics substep for the vehicle's chassis position.
 */
export interface RoadSegmentIndex {
  segments: RoadSegment[];
  cells: number[][];
  cellsPerSide: number;
  cellSize: number;
  /** Min corner of the grid in chunk-local meters. */
  minLocal: number;
}

const TARGET_CELL_SIZE = 50;

export function buildRoadSegmentIndex(payload: ChunkPayload): RoadSegmentIndex {
  const segments: RoadSegment[] = [];
  for (const way of payload.osm.ways) {
    if (way.nodes.length < 2) continue;
    const halfWidth = roadWidthMeters(way.roadClass) * 0.5;
    const drivable = isDrivable(way.roadClass);
    let prevX: number | null = null;
    let prevZ: number | null = null;
    for (const nodeId of way.nodes) {
      const node = payload.osm.nodes[nodeId];
      if (!node) {
        prevX = null;
        prevZ = null;
        continue;
      }
      const { x, z } = latLngToLocalMeters(node.lat, node.lng, payload.origin.lat, payload.origin.lng);
      if (prevX !== null && prevZ !== null) {
        segments.push({
          ax: prevX,
          az: prevZ,
          bx: x,
          bz: z,
          halfWidth,
          drivable,
          roadClass: way.roadClass,
          lanes: way.lanes,
          oneway: way.oneway,
          bridge: way.bridge === true,
          tunnel: way.tunnel === true,
        });
      }
      prevX = x;
      prevZ = z;
    }
  }

  const size = payload.sizeMeters;
  const cellsPerSide = Math.max(1, Math.round(size / TARGET_CELL_SIZE));
  const cellSize = size / cellsPerSide;
  const minLocal = -size * 0.5;
  const cells: number[][] = new Array(cellsPerSide * cellsPerSide);
  for (let i = 0; i < cells.length; i++) cells[i] = [];

  for (let i = 0; i < segments.length; i++) {
    const s = segments[i]!;
    const margin = s.halfWidth;
    const minSegX = Math.min(s.ax, s.bx) - margin;
    const maxSegX = Math.max(s.ax, s.bx) + margin;
    const minSegZ = Math.min(s.az, s.bz) - margin;
    const maxSegZ = Math.max(s.az, s.bz) + margin;
    const cx0 = clampInt(Math.floor((minSegX - minLocal) / cellSize), 0, cellsPerSide - 1);
    const cx1 = clampInt(Math.floor((maxSegX - minLocal) / cellSize), 0, cellsPerSide - 1);
    const cz0 = clampInt(Math.floor((minSegZ - minLocal) / cellSize), 0, cellsPerSide - 1);
    const cz1 = clampInt(Math.floor((maxSegZ - minLocal) / cellSize), 0, cellsPerSide - 1);
    for (let cz = cz0; cz <= cz1; cz++) {
      for (let cx = cx0; cx <= cx1; cx++) {
        cells[cz * cellsPerSide + cx]!.push(i);
      }
    }
  }

  return { segments, cells, cellsPerSide, cellSize, minLocal };
}

/**
 * Return what surface a chunk-local point is on. Drivable wins ties so that a footway
 * crossing a road still reads as road (the car is, physically, on tarmac there).
 */
export function classifySurfaceAt(
  idx: RoadSegmentIndex,
  localX: number,
  localZ: number,
  margin = 0,
): SurfaceKind {
  const cx = clampInt(Math.floor((localX - idx.minLocal) / idx.cellSize), 0, idx.cellsPerSide - 1);
  const cz = clampInt(Math.floor((localZ - idx.minLocal) / idx.cellSize), 0, idx.cellsPerSide - 1);
  const bucket = idx.cells[cz * idx.cellsPerSide + cx];
  if (!bucket || bucket.length === 0) return 'grass';

  let onPath = false;
  for (let i = 0; i < bucket.length; i++) {
    const s = idx.segments[bucket[i]!]!;
    const distSq = pointSegmentDistSq(localX, localZ, s.ax, s.az, s.bx, s.bz);
    // Expand the matched width by `margin` so callers (e.g. grass scatter) can keep clear of
    // the shoulder/sidewalk strips and the smoothed-centerline bulge that extend past the bare
    // OSM road half-width. margin=0 (default) is the exact tarmac edge used by physics.
    const r = s.halfWidth + margin;
    if (distSq <= r * r) {
      if (s.drivable) return 'road';
      onPath = true;
    }
  }
  return onPath ? 'path' : 'grass';
}

/**
 * Like `classifySurfaceAt`, but returns the OSM metadata of the matching segment instead
 * of a coarse surface kind. Drivable segments win ties. Returns null when nothing matches
 * (i.e. the point is on grass).
 *
 * Used by the road-class debug HUD to show what kind of road the car is currently on.
 */
export function classifyRoadDetailAt(
  idx: RoadSegmentIndex,
  localX: number,
  localZ: number,
): RoadDetail | null {
  const cx = clampInt(Math.floor((localX - idx.minLocal) / idx.cellSize), 0, idx.cellsPerSide - 1);
  const cz = clampInt(Math.floor((localZ - idx.minLocal) / idx.cellSize), 0, idx.cellsPerSide - 1);
  const bucket = idx.cells[cz * idx.cellsPerSide + cx];
  if (!bucket || bucket.length === 0) return null;

  let bestPath: RoadSegment | null = null;
  for (let i = 0; i < bucket.length; i++) {
    const s = idx.segments[bucket[i]!]!;
    const distSq = pointSegmentDistSq(localX, localZ, s.ax, s.az, s.bx, s.bz);
    const rSq = s.halfWidth * s.halfWidth;
    if (distSq <= rSq) {
      if (s.drivable) return segmentToDetail(s);
      if (!bestPath) bestPath = s;
    }
  }
  return bestPath ? segmentToDetail(bestPath) : null;
}

function segmentToDetail(s: RoadSegment): RoadDetail {
  return {
    roadClass: s.roadClass,
    lanes: s.lanes,
    oneway: s.oneway,
    bridge: s.bridge,
    tunnel: s.tunnel,
    drivable: s.drivable,
    halfWidth: s.halfWidth,
  };
}

function pointSegmentDistSq(px: number, pz: number, ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax;
  const dz = bz - az;
  const lenSq = dx * dx + dz * dz;
  if (lenSq < 1e-9) {
    const ex = px - ax;
    const ez = pz - az;
    return ex * ex + ez * ez;
  }
  let t = ((px - ax) * dx + (pz - az) * dz) / lenSq;
  if (t < 0) t = 0;
  else if (t > 1) t = 1;
  const cx = ax + t * dx;
  const cz = az + t * dz;
  const ex = px - cx;
  const ez = pz - cz;
  return ex * ex + ez * ez;
}

function clampInt(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}
